// src/pages/PaymentSuccessPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API from '../api';
import { toast } from 'react-toastify';

export default function PaymentSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser, updateUser } = useAuth();
  
  const [status, setStatus] = useState('verifying');
  const [message, setMessage] = useState('Verifying your payment details...');
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [debugInfo, setDebugInfo] = useState({});
  
  const verificationAttempted = useRef(false);

  useEffect(() => {
    const verifyPayment = async () => {
      if (verificationAttempted.current) return;
      verificationAttempted.current = true;

    let oid = searchParams.get('oid');
let data = searchParams.get('data');

if (oid && oid.includes('?')) {
  const parts = oid.split('?');
  oid = parts[0]; 
  if (!data && parts[1] && parts[1].includes('data=')) {
    data = parts[1].split('data=')[1];
  }
}

if (!data) {
  const url = window.location.href;
  const dataMatch = url.match(/[?&]data=([^#&]+)/);
  if (dataMatch) data = dataMatch[1];
}

      
      setDebugInfo({ oid, hasData: !!data, url: window.location.href });

      if (!oid) {
        setStatus('error');
        setMessage('Invalid payment reference. Order ID missing.');
        return;
      }

      try {
        const response = await API.post('/payments/verify', {
          oid,
          data: data || undefined
        });

        if (response.data.success) {
          setStatus('success');
          setMessage('Your payment has been successfully verified.');
          setSubscriptionDetails(response.data.subscription);
          setPaymentDetails(response.data.payment);

          if (response.data.user) {
            updateUser(response.data.user);
          }
          await refreshUser();
          localStorage.removeItem('pendingPayment');
          toast.success('Subscription Active!');

        } else {
          throw new Error(response.data.message || 'Verification failed');
        }

      } catch (error) {
        if (error.response?.data?.message?.includes('verified') || 
            error.response?.data?.alreadyProcessed) {
          setStatus('success');
          setMessage('Payment was already verified successfully.');
          refreshUser().catch(e => console.log(e));
        } else {
          setStatus('error');
          setMessage(error.response?.data?.message || 'Payment verification failed.');
        }
      }
    };

    verifyPayment();
  }, [searchParams, refreshUser, updateUser]);

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  return (
    <div className="payment-container">
      {/* CSS Styles */}
      <style>{`
        .payment-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: #f9fafb;
          padding: 20px;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        }

        .card {
          background: white;
          width: 100%;
          max-width: 450px;
          border-radius: 24px;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
          overflow: hidden;
          transition: transform 0.3s ease;
        }

        .card:hover {
          transform: translateY(-2px);
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }

        .header {
          padding: 40px 30px 20px;
          text-align: center;
        }

        .icon-wrapper {
          width: 80px;
          height: 80px;
          margin: 0 auto 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
        }

        .success-icon {
          background-color: #d1fae5;
          color: #059669;
          animation: bounce 1s ease-in-out;
        }

        .error-icon {
          background-color: #fee2e2;
          color: #dc2626;
        }

        .loading-spinner {
          width: 60px;
          height: 60px;
          border: 4px solid #f3f4f6;
          border-top: 4px solid #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 24px;
        }

        .title {
          font-size: 24px;
          font-weight: 700;
          margin-bottom: 12px;
          color: #111827;
        }
        
        .title.error { color: #dc2626; }
        .title.success { color: #059669; }

        .message {
          color: #6b7280;
          font-size: 15px;
          line-height: 1.5;
        }

        .receipt-section {
          padding: 0 30px 20px;
        }

        .receipt-card {
          background-color: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          padding: 24px;
          position: relative;
        }

        .receipt-title {
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: #94a3b8;
          font-weight: 700;
          text-align: center;
          margin-bottom: 16px;
        }

        .receipt-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 12px;
          font-size: 14px;
        }

        .label { color: #64748b; }
        .value { font-weight: 600; color: #334155; }
        .value.highlight { color: #059669; }
        .value.large { font-size: 18px; color: #0f172a; }

        .divider {
          border-bottom: 1px dashed #cbd5e1;
          margin: 12px 0;
        }

        .debug-box {
          background: #fef2f2;
          border: 1px solid #fecaca;
          padding: 15px;
          border-radius: 12px;
          font-size: 12px;
          color: #b91c1c;
          text-align: left;
          margin: 0 30px;
          word-break: break-all;
        }

        .footer {
          padding: 20px 30px 30px;
        }

        .btn {
          width: 100%;
          padding: 16px;
          border-radius: 12px;
          font-weight: 600;
          font-size: 16px;
          cursor: pointer;
          border: none;
          transition: all 0.2s;
        }

        .btn-primary {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          box-shadow: 0 4px 6px -1px rgba(16, 185, 129, 0.3);
        }

        .btn-primary:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 8px -1px rgba(16, 185, 129, 0.4);
        }

        .btn-secondary {
          background: #f3f4f6;
          color: #374151;
        }

        .btn-secondary:hover { background: #e5e7eb; }

        .btn-danger {
          background: #ef4444;
          color: white;
        }

        .grid-buttons {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }

        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @keyframes bounce { 
          0%, 100% { transform: translateY(0); } 
          50% { transform: translateY(-10px); } 
        }
      `}</style>

      <div className="card">
        
        {/* Top Section */}
        <div className="header">
          {/* Status Icons */}
          {status === 'verifying' && (
            <div className="loading-spinner"></div>
          )}

          {status === 'success' && (
            <div className="icon-wrapper success-icon">
              <svg width="40" height="40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
          )}

          {status === 'error' && (
            <div className="icon-wrapper error-icon">
              <svg width="40" height="40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </div>
          )}

          <h1 className={`title ${status}`}>
            {status === 'verifying' && 'Processing...'}
            {status === 'success' && 'Payment Successful!'}
            {status === 'error' && 'Payment Failed'}
          </h1>
          
          <p className="message">{message}</p>
        </div>

        {/* Receipt Section */}
        {status === 'success' && subscriptionDetails && (
          <div className="receipt-section">
            <div className="receipt-card">
              <div className="receipt-title">Subscription Receipt</div>
              
              <div className="receipt-row">
                <span className="label">Plan Name</span>
                <span className="value large">{subscriptionDetails.planName}</span>
              </div>

              <div className="divider"></div>
              
              <div className="receipt-row">
                <span className="label">Start Date</span>
                <span className="value">{formatDate(subscriptionDetails.startDate)}</span>
              </div>
              <div className="receipt-row">
                <span className="label">Expiration</span>
                <span className="value highlight">{formatDate(subscriptionDetails.endDate)}</span>
              </div>

              {paymentDetails?.amount && (
                 <>
                   <div className="divider"></div>
                   <div className="receipt-row" style={{marginBottom: 0}}>
                     <span className="label">Amount Paid</span>
                     <span className="value">Rs. {paymentDetails.amount}</span>
                   </div>
                 </>
              )}
            </div>
          </div>
        )}

        {/* Debug Info */}
        {status === 'error' && (
           <div className="debug-box">
             <strong>Error ID:</strong> {debugInfo.oid || 'N/A'}
           </div>
        )}

        {/* Buttons */}
        <div className="footer">
          {status === 'success' ? (
            <button
              onClick={() => navigate('/dashboard')}
              className="btn btn-primary"
            >
              Continue to Dashboard
            </button>
          ) : status === 'error' ? (
            <div className="grid-buttons">
              <button
                onClick={() => navigate('/dashboard')}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => navigate('/subscription')}
                className="btn btn-danger"
              >
                Try Again
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}